import {
  Callout,
  Figure,
  H2,
  H3,
  InfoTable,
  LI,
  P,
  Table,
  UL,
} from "../../components/blog/BlogTypography";

import affiliateProgramForumPost from "../../assets/blog/vexy-ransomware/affiliate-program-forum-post.png";
import controlPanelAffiliateEconomics from "../../assets/blog/vexy-ransomware/control-panel-affiliate-economics.png";
import leakSiteListingsPart1 from "../../assets/blog/vexy-ransomware/leak-site-listings-part1.png";
import leakSiteListingsPart2 from "../../assets/blog/vexy-ransomware/leak-site-listings-part2.png";

const ASSESSMENT_ROWS = [
  ["Threat actor", "Vexy / VEXY Ransomware"],
  ["First public evidence", "Underground RaaS advertisement, 3 September 2026"],
  ["Motivation", "Financial / extortion"],
  ["Operating model", "Advertised affiliate / Ransomware-as-a-Service program"],
  ["Platforms advertised", "Windows, Linux, VMware ESXi (claimed Rust implementation)"],
  ["Affiliate onboarding", "Invite-based; one-time US$200 fee; Bitcoin payment stated in the advertisement"],
  ["Claimed leak-site victims", "10 visible listings as of 12 September 2026, ~279.7 GB combined claimed volume"],
  ["Geography concentration", "7 of 10 visible claims are Indian organizations or India-linked entities"],
  [
    "CyberMon assessment",
    "Moderate confidence in operational existence; high confidence an underground RaaS advertisement exists; low-to-moderate confidence in specific technical claims until malware or forensic evidence becomes available.",
  ],
];

const PROFILE_ROWS = [
  ["Name / aliases", "Vexy Ransomware; VEXY"],
  ["Motivation", "Financial / extortion"],
  ["Operating model", "Advertised affiliate / RaaS model"],
  ["Programming language claimed", "Rust"],
  ["Platforms advertised", "Windows, Linux, VMware ESXi"],
  ["Extortion model", "Leak-site based extortion; double-extortion is plausible from site design"],
  ["Access / control plane", "Tor-based control panel advertised; separate Tor leak site observed"],
  ["Affiliate onboarding", "Invite-based; US$200 one-time fee; Bitcoin payment stated in advertisement"],
  ["Contact", "qTox identifier advertised in the underground post"],
  ["Attribution", "No reliable country-of-origin or established-group affiliation identified"],
  ["Maturity", "Emerging / early-stage operation with rapidly increasing public victim claims"],
];

const VICTIM_HEADERS = ["Date", "Claimed victim", "Geography", "Sector", "Claimed volume", "Site status"];
const VICTIM_ROWS = [
  ["2 Sep", "Engefitas", "Brazil", "Manufacturing / adhesives", "27.25 GB", "Published"],
  ["3 Sep", "McDonald's Ecuador", "Ecuador", "Hospitality / franchise", "42.08 GB", "Active countdown"],
  ["4 Sep", "Palsana Enviro (PEPL)", "India", "Environmental / industrial wastewater", "220 MB", "Active countdown"],
  ["4 Sep", "Annapurna Fashion", "India", "Textiles / apparel", "3.48 GB", "Active countdown"],
  ["4 Sep", "Sancity Soft Touch", "India-linked", "IT services", "100 MB", "Active countdown"],
  ["5 Sep", "Mega Velocity", "India-linked", "Technology / hosting", "46.68 GB", "Active countdown"],
  ["6 Sep", "Sancity", "India", "Real estate / construction", "130 MB", "Active countdown"],
  ["7 Sep", "United Group", "India", "Diversified business group", "116 GB", "Active countdown"],
  ["9 Sep", "Logar Network Solutions", "Brazil", "Managed IT / MSP", "55 GB", "Active countdown"],
  ["10 Sep", "i2k2 Networks", "India", "Cloud / hosting / managed IT", "100 GB", "Active countdown"],
];

const INDIA_HEADERS = ["Claimed victim", "Business profile", "Risk relevance"];
const INDIA_ROWS = [
  [
    "i2k2 Networks",
    "Cloud, hosting, managed IT, data center, DR and DevOps",
    "Potential concentration of downstream/customer data; high supply-chain relevance",
  ],
  ["United Group", "Diversified Indian business group", "Broad corporate data estate; sector diversity"],
  ["Sancity", "Real estate / construction", "Financial, project, customer and contractual data"],
  [
    "Mega Velocity",
    "Software / consultancy / hosting per the supplied description",
    "Potential privileged infrastructure and customer-data exposure",
  ],
  [
    "Sancity Soft Touch",
    "IT services / application / payment gateway / cloud",
    "Potential access to customer and application data",
  ],
  ["Annapurna Fashion", "Textiles / apparel manufacturing and distribution", "Operational and commercial IP"],
  [
    "Palsana Enviro",
    "Industrial wastewater / CETP",
    "Industrial operations and environmental/compliance records",
  ],
];

const ATTACK_HEADERS = ["Technique", "ATT&CK ID", "Evidence status", "Rationale"];
const ATTACK_ROWS = [
  ["Data Encrypted for Impact", "T1486", "Advertised", "AES-256 encryption is explicitly claimed."],
  [
    "Inhibit System Recovery",
    "T1490",
    "Advertised / implied",
    "Free-space wiping and destructive recovery-related behaviour are advertised; VSS deletion is not in the primary forum post.",
  ],
  ["Service Stop", "T1489", "Advertised", "Windows/Linux service and process termination is explicitly advertised."],
  [
    "File and Directory Discovery",
    "T1083",
    "Implied",
    "Selective file/folder targeting is advertised, but discovery telemetry is unavailable.",
  ],
  [
    "Network Share Discovery / targeting",
    "T1135 / related",
    "Advertised capability",
    "SMB/NFS network-share encryption is advertised.",
  ],
  ["Indicator Removal on Host", "T1070", "Advertised", "Self-deletion and event-log clearing are advertised."],
  [
    "Impair Defenses",
    "T1562.001",
    "Possible",
    "Service/process management may affect security tooling, but EDR tampering is not demonstrated.",
  ],
  [
    "Exfiltration Over Web Service",
    "T1041 / related",
    "Extortion model suggests",
    "Leak-site publication implies data exfiltration may occur, but the transfer path is not documented.",
  ],
  ["Virtualization/Sandbox Evasion", "N/A", "Not established", "No reliable evidence in the reviewed material."],
];

const VECTOR_HEADERS = ["Potential vector", "Evidence level", "Defensive priority"];
const VECTOR_ROWS = [
  [
    "Stolen credentials / infostealer-derived access",
    "Hypothesis; some tracker telemetry suggests relevance",
    "High — monitor privileged and remote-access credentials",
  ],
  ["Internet-facing edge / VPN exploitation", "Hypothesis", "High — validate patching and exposure"],
  ["RMM/MSP compromise", "Hypothesis", "High for IT-service-heavy victims"],
  ["RDP / password spraying", "Hypothesis", "High — remove internet exposure and enforce MFA"],
  ["Phishing / malicious documents", "Generic ransomware vector; not Vexy-specific", "Medium"],
];

const HUNT_HEADERS = ["Hunt theme", "Example signal", "Why it matters"];
const HUNT_ROWS = [
  ["Remote execution", "WMI/remote service execution from unusual admin sources", "Potential lateral movement before detonation"],
  ["Archive staging", "7z/RAR/ZIP creation of unusually large archives on servers", "Potential exfiltration preparation"],
  ["Recovery tampering", "Unexpected shadow-copy or backup-management changes", "May precede destructive impact"],
  ["Credential anomalies", "Password spray, impossible travel, new privileged sessions", "Possible stolen-credential access"],
  ["RMM drift", "New agent or session outside change window", "Potential MSP/RMM entry path"],
  ["Bulk egress", "Large outbound transfer from sensitive servers", "Potential double-extortion preparation"],
];

const CAPABILITY_HEADERS = ["CyberMon capability", "Vexy use case"];
const CAPABILITY_ROWS = [
  [
    "Dark Web Intelligence",
    "Monitor Vexy leak-site claims, emerging aliases, underground recruitment posts, victim mentions and data-publication events.",
  ],
  [
    "Attack Surface Intelligence",
    "Identify internet-facing VPN, RMM, remote-access and administrative services that could become attractive entry points.",
  ],
  [
    "Credential exposure monitoring",
    "Detect exposed corporate credentials and stealer-log references associated with monitored domains.",
  ],
  ["Brand / executive monitoring", "Track references to organizations and leadership on emerging extortion channels."],
  [
    "Third-party risk intelligence",
    "Monitor MSPs, hosting providers and technology partners whose compromise could create downstream exposure.",
  ],
];

const CONFIDENCE_HEADERS = ["Claim / finding", "Confidence", "Reason"];
const CONFIDENCE_ROWS = [
  ["Vexy exists as an active extortion brand", "High", "Primary leak-site snapshot plus multiple independent trackers"],
  ["Vexy advertises an affiliate/RaaS program", "High", "Direct underground forum advertisement"],
  ["US$200 affiliate invitation fee is advertised", "High", "Direct underground forum advertisement"],
  ["Rust multi-platform lockers exist", "Low–Moderate", "Advertised by the actor; no independent sample analysis"],
  ["AES-256/RSA implementation is used in real attacks", "Low", "Advertisement only"],
  ["Victim organizations were actually compromised", "Low–Moderate", "Leak-site claims corroborated as listings, not as breaches"],
  [
    "India is a meaningful early target geography",
    "Moderate–High",
    "Large share of the supplied victim snapshot; later trackers also identify multiple Indian claims",
  ],
  ["Specific initial-access vector", "Low", "No victim-level forensic evidence in the reviewed material"],
  ["Established-group affiliation / rebrand", "Low", "No reliable attribution evidence"],
];

export default function VexyRansomwareThreatIntel() {
  return (
    <>
      <Callout>
        Analysis based on an underground-forum advertisement dated 3 September 2026 and a Vexy
        leak-site snapshot captured 12 September 2026, prepared by CyberMon Threat Intelligence
        Research. Distinguish throughout: what Vexy <em>claims</em> its tooling can do versus what
        has been <em>observed</em> — a leak-site listing is a claim of compromise, not independent
        proof of it.
      </Callout>

      <H2>Executive Summary</H2>
      <P>
        Vexy is an emerging ransomware/extortion operation first publicly visible in September
        2026. CyberMon's review of an underground-forum advertisement dated 3 September 2026 and a
        Vexy leak-site snapshot with posts starting 2 September 2026 indicates a developing
        operation with a strong India and Latin America footprint. The underground advertisement
        explicitly markets an affiliate program and describes Windows, Linux and ESXi lockers, a
        Tor-accessible control panel, automated Bitcoin-based registration and a one-time US$200
        invitation fee to log into the portal.
      </P>
      <P>
        The most important analytical distinction is between capability claims and observed
        behaviour. The forum advertisement describes what the Vexy group says its tooling can do.
        The leak site demonstrates that Vexy is publishing victim claims with countdown timers and
        claimed data volumes, but it does not independently prove that the advertised encryption
        features were used.
      </P>
      <P>
        As of 12 September 2026, the Vexy leak site contained ten visible listings, including
        seven Indian organizations: i2k2 Networks, United Group, Sancity, Mega Velocity and Sancity
        Soft Touch, plus Annapurna Fashion and Palsana Enviro in the same early campaign window.
      </P>

      <H3>At-a-Glance Assessment</H3>
      <InfoTable rows={ASSESSMENT_ROWS} />

      <H2>Vexy Threat-Actor Profile</H2>
      <InfoTable rows={PROFILE_ROWS} />

      <H2>Underground RaaS Advertisement: What Vexy Claims to Offer</H2>
      <P>
        The underground forum post is the most important primary-source artifact in this
        assessment. It was posted by the account "vexys" on 3 September 2026 under the title
        "[RaaS] Vexy Ransomware." The advertisement explicitly uses the phrase "Vexy Affiliate
        Program" and presents a multi-platform locker ecosystem.
      </P>

      <Figure
        src={affiliateProgramForumPost}
        alt="Underground forum post titled '[RaaS] Vexy Ransomware' by user vexys, posted Sep 3, 2026, headed 'Vexy Affiliate Program' and listing Rust-based Windows, Linux and ESXi locker features including high-speed hybrid encryption, disk and network-share coverage, selective targeting and exclusions, service and process management, free-space wiping, stealth/persistence control, system customization, automated printer notification and monitoring."
        caption="Figure 1: The underground forum advertisement for the Vexy Affiliate Program, describing Rust-based Windows, Linux and ESXi lockers. This is the actor's own capability claim, not independent proof it was used in any listed intrusion."
      />

      <H3>Windows locker</H3>
      <UL>
        <LI>Rust implementation is claimed.</LI>
        <LI>AES-256 is claimed for bulk data encryption, with RSA used for key wrapping.</LI>
        <LI>Local disks and network shares are claimed targets.</LI>
        <LI>Operators are offered selective exclusions for folders, files and extensions.</LI>
        <LI>Service and process termination is advertised, potentially allowing interference with applications or protective services.</LI>
        <LI>Free-space wiping is advertised as a trace/data-removal feature.</LI>
        <LI>Self-deletion after execution is advertised.</LI>
        <LI>Wallpaper/icon customization and automated printer notification are advertised as victim-facing features.</LI>
      </UL>

      <H3>Linux and ESXi capabilities</H3>
      <UL>
        <LI>
          Linux: local data plus SMB/NFS network shares, selective exclusions, service
          termination, free-space wiping, monitoring and self-deletion.
        </LI>
        <LI>
          ESXi: VMware datastore targeting, VMware service termination, selective targeting,
          self-deletion and event-log clearing.
        </LI>
      </UL>

      <H2>Control Panel and Affiliate Economics</H2>
      <UL>
        <LI>Tor-only control-panel access.</LI>
        <LI>Build configuration and compilation are advertised as operator functions.</LI>
        <LI>Analytics, earnings monitoring, client communication, ticketing and account management are advertised.</LI>
        <LI>24/7 support, multilingual functionality and a resource center are advertised.</LI>
        <LI>Access is described as invite-only with a one-time US$200 registration fee.</LI>
        <LI>Bitcoin is the stated payment mechanism, with automatic wallet/payment processing described in the advertisement.</LI>
      </UL>

      <Figure
        src={controlPanelAffiliateEconomics}
        alt="Underground forum post section titled 'Control Panel' listing Tor network compatibility, build configuration tool, 24/7 customer support, detailed analytics, unique account management, client communication, comprehensive resource center, operational dashboard, account preferences, news and blog, multiple language support, and secure session management; followed by a 'How to Get Access' section describing an easy invite mechanism, a one-time $200 USD invitation cost, instant delivery, cryptocurrency (Bitcoin) payment, a fully automatic process, and registration via invite code."
        caption="Figure 2: The advertisement's control-panel feature list and affiliate-onboarding terms — a one-time $200 invite fee, Bitcoin payment, and an automated, human-free signup flow."
      />

      <P>
        The US$200 invitation cost is low compared with the economic barrier associated with
        established RaaS programs, which may indicate an early-stage affiliate-acquisition
        strategy rather than a mature, vetted affiliate operation.
      </P>

      <H2>Observed Leak-Site Activity</H2>
      <P>
        The leak site lists ten visible victim cards, each providing a victim name, website,
        claimed data volume, business description, listing timestamp and a countdown or
        publication status. The visible listings represent approximately 279.7 GB of claimed
        data. The pattern is notable for its concentration on smaller and mid-market
        organizations and for repeated targeting of Indian businesses.
      </P>

      <Figure
        src={leakSiteListingsPart1}
        alt="Vexy leak-site 'Leaked Data' grid showing six victim cards with countdown timers: i2k2 Networks (100 GB, https://i2k2.com), Logar Network Solutions (55 GB, https://logar.com.br), United Group (116 GB, https://united-group.in), Sancity (130 MB, sancity.in), Mega Velocity (46.68 GB, https://megavelocity.net), and Sancity Soft Touch (100 MB, softtouch4u.com), each with a business description and a UTC listing timestamp."
        caption="Figure 3: Six of the ten claimed victims on the Vexy leak site, each shown with its countdown timer, claimed data volume, and listing timestamp."
      />

      <Figure
        src={leakSiteListingsPart2}
        alt="Vexy leak-site 'Leaked Data' grid continued, showing four more victim cards: Annapurna Fashion (3.48 GB, annapurnafashion.com), Palsana Enviro / PEPL (220 MB, palsanaenviro.com), McDonalds Ecuador (42.08 GB, https://mcdonalds.com.ec), and Engefitas, marked 'PUBLISHED' (27.25 GB, engefitas.com.br)."
        caption="Figure 4: The remaining four claimed victims, including Engefitas — the one listing marked 'Published' rather than under an active countdown."
      />

      <Table headers={VICTIM_HEADERS} rows={VICTIM_ROWS} />

      <H2>India Exposure: Why the Early Campaign Matters</H2>
      <P>
        India is the most visible geography in Vexy's early campaign. The claimed victims span
        environmental services, textiles, IT services, technology/hosting, real estate and
        diversified business operations — a breadth suggesting the actor may be selecting
        organizations based more on reachable attack surface and monetizable data than on a single
        vertical.
      </P>
      <Table headers={INDIA_HEADERS} rows={INDIA_ROWS} />

      <H2>What Remains Unproven</H2>
      <UL>
        <LI>Whether the advertised lockers have actually been deployed in the listed intrusions.</LI>
        <LI>Whether Vexy Group itself developed the encryptors or uses a third-party/leased codebase.</LI>
        <LI>The actual initial-access vector for any listed victim.</LI>
        <LI>Whether data was encrypted, exfiltrated, both, or in some cases only claimed.</LI>
        <LI>The authenticity and completeness of the claimed data volumes.</LI>
        <LI>The existence of a stable affiliate population rather than a single operator marketing an affiliate model.</LI>
      </UL>

      <H2>MITRE ATT&amp;CK Mapping — Advertised and Assessed Behaviours</H2>
      <P>
        Techniques marked "advertised" are derived from the underground post; they should not be
        represented as confirmed Vexy telemetry until samples or forensic reports are available.
      </P>
      <Table headers={ATTACK_HEADERS} rows={ATTACK_ROWS} />

      <H2>Initial Access: Evidence Versus Hypothesis</H2>
      <P>
        External analysis has proposed edge-appliance exploitation, RMM compromise, exposed RDP,
        credential abuse and phishing as plausible pathways. None of these is confirmed for any
        specific Vexy-claimed victim.
      </P>
      <Table headers={VECTOR_HEADERS} rows={VECTOR_ROWS} />

      <H2>Defensive Detection Priorities</H2>
      <P>
        Organizations should focus on behaviours that precede or accompany ransomware deployment
        rather than waiting for a Vexy-specific hash.
      </P>
      <UL>
        <LI>Monitor unusual authentication bursts against VPN, RDP and externally exposed services, especially involving privileged or service accounts.</LI>
        <LI>Alert on new remote-management software, unexpected RMM sessions and administrative connections outside approved maintenance windows.</LI>
        <LI>Detect abnormal remote process creation, newly installed services and administrative execution across multiple servers.</LI>
        <LI>Alert on sudden archive creation or large staging directories on file servers and application servers.</LI>
        <LI>Monitor destructive recovery activity and unexpected changes to backup infrastructure.</LI>
        <LI>Watch for security-control degradation, service termination and attempts to remove local traces.</LI>
        <LI>Monitor unusual outbound data transfers from file servers, databases and cloud-management hosts.</LI>
        <LI>Maintain rapid isolation capability for the first suspected staging host; do not wait for encryption to begin.</LI>
      </UL>

      <H3>Recommended SIEM / EDR Hunt Themes</H3>
      <Table headers={HUNT_HEADERS} rows={HUNT_ROWS} />

      <H2>Incident Response Priorities</H2>
      <UL>
        <LI>Preserve evidence from the suspected patient-zero system, identity provider, VPN/RMM infrastructure and file servers.</LI>
        <LI>Immediately rotate privileged credentials and invalidate active sessions if compromise is suspected.</LI>
        <LI>Isolate backup infrastructure and verify that immutable/offline copies remain inaccessible to compromised administrative identities.</LI>
        <LI>Block unnecessary east-west SMB/RDP/WMI paths while maintaining required business connectivity.</LI>
        <LI>Preserve the Vexy leak-site listing, timestamps, claimed volumes and screenshots as evidentiary artifacts; do not access or redistribute stolen data.</LI>
        <LI>Determine whether an incident involved data theft, encryption, both, or only an unsubstantiated claim.</LI>
        <LI>Engage legal/privacy teams early where regulated personal or customer information may have been accessed.</LI>
      </UL>

      <H2>CyberMon Perspective: Why Vexy Matters</H2>
      <P>
        Vexy is a useful example of why Digital Risk Intelligence and Attack Surface Intelligence
        should be treated as complementary controls. The public evidence does not yet reveal a
        stable malware signature, but the operation's victim-selection and leak-site activity can
        be monitored externally.
      </P>
      <Table headers={CAPABILITY_HEADERS} rows={CAPABILITY_ROWS} />

      <H2>CyberMon Confidence Assessment</H2>
      <Table headers={CONFIDENCE_HEADERS} rows={CONFIDENCE_ROWS} />

      <H2>Threat Outlook</H2>
      <P>
        Vexy's next stage of development will be defined less by the quality of its advertisement
        and more by whether it demonstrates repeatable operational capability. The most important
        indicators to watch are sustained victim cadence, verified publication of samples,
        emergence of a stable affiliate community, reusable malware artefacts, confirmed
        initial-access patterns, infrastructure reuse, and movement into larger or more sensitive
        sectors.
      </P>
      <UL>
        <LI>Near term: expect additional leak-site claims as Vexy attempts to establish credibility with affiliates and victims.</LI>
        <LI>Medium term: a genuine RaaS model should produce recurring victimology patterns and potentially multiple affiliate-specific operational signatures.</LI>
        <LI>Escalation indicator: appearance of validated samples, ransom notes, reusable infrastructure or confirmed compromise narratives would materially raise confidence in the group's technical maturity.</LI>
        <LI>India-specific concern: the early concentration of claims makes Indian mid-market organizations a sensible monitoring priority, particularly those with exposed remote access, RMM dependencies, hosting infrastructure or weak credential hygiene.</LI>
      </UL>
      <Callout>
        <strong>Bottom line:</strong> Vexy should be treated as an emerging threat worth monitoring
        now, not as an already-established ransomware family with fully understood TTPs. The
        underground RaaS advertisement is significant because it shows intent to recruit and
        operationalize affiliates; the leak site is significant because it shows an active
        extortion workflow.
      </Callout>

      <H2>Conclusion</H2>
      <P>
        Vexy's claimed victim list should currently be treated as a serious but unverified set of
        cybercriminal claims. The concentration of Indian mid-market targets is a meaningful early
        signal regardless of how the technical capability claims eventually hold up, because
        victim-selection patterns tend to persist even as tooling evolves. The appropriate response
        is to monitor now, validate quickly if named, and avoid repeating attacker claims as
        confirmed facts.
      </P>
      <P>
        For security teams, the broader lesson is the same one every emerging RaaS brand teaches:
        external threat monitoring and attack-surface visibility must operate together. Knowing
        that criminals are discussing or claiming your organization matters only if it is paired
        with reducing the externally exposed weaknesses — VPNs, RMM tooling, RDP, credentials —
        that let an advertised capability become a real intrusion.
      </P>
    </>
  );
}
